import NumberInput from '../../../components/NumberInput'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { estimateKcal } from '../../../lib/cardioCalories'

const TYPES = [
  'elliptical',
  'treadmill_walk',
  'treadmill_run',
  'treadmill_intervals',
  'bike',
  'stairmaster',
  'swim',
]

// Per-type field visibility — keeps the UI from asking for "incline"
// on an elliptical or "speed" on a stationary bike. Swim cares about
// distance + calories (laps × pool length); level/incline/speed are
// pool-irrelevant.
const TYPE_FIELDS = {
  elliptical:          { level: true,  incline: false, speed: false, distance: true,  calories: true },
  treadmill_walk:      { level: false, incline: true,  speed: true,  distance: true,  calories: true },
  treadmill_run:       { level: false, incline: true,  speed: true,  distance: true,  calories: true },
  treadmill_intervals: { level: false, incline: true,  speed: true,  distance: true,  calories: true },
  bike:                { level: true,  incline: false, speed: false, distance: true,  calories: true },
  stairmaster:         { level: true,  incline: false, speed: false, distance: false, calories: true },
  swim:                { level: false, incline: false, speed: false, distance: true,  calories: true },
}

function NumField({ label, value, onChange, step = '0.5' }) {
  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <NumberInput
        inputMode="decimal"
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-base sm:text-sm"
        dir="ltr"
        aria-label={label}
      />
    </div>
  )
}

export default function CardioBlock({ cardio, onChange, onRemove, bodyWeightKg }) {
  const { t } = useTranslation()
  const fields = TYPE_FIELDS[cardio.cardio_type] || TYPE_FIELDS.elliptical

  // Apple-Watch-style auto-fill for calories. If the user hasn't
  // explicitly typed a kcal value, we estimate from MET × weight ×
  // duration whenever the type/duration/speed/level/weight inputs are
  // sufficient. Once the user types into the kcal field directly, we
  // stop touching it. Clearing the field re-enables auto.
  const userSetKcalRef = useRef(
    cardio.calories_estimated != null && cardio.calories_estimated !== ''
  )

  // Wrap the calories patch so we can detect the "user typed" path —
  // any other patch to the block can't be a user kcal edit.
  const handleCaloriesChange = (val) => {
    userSetKcalRef.current = val !== ''
    onChange({ calories_estimated: val })
  }

  useEffect(() => {
    if (userSetKcalRef.current) return
    const kcal = estimateKcal({
      cardioType: cardio.cardio_type,
      durationMin: cardio.duration_min,
      speed: cardio.speed,
      level: cardio.level,
      bodyWeightKg,
    })
    if (kcal == null) return
    const next = String(kcal)
    // Guard against the self-update loop: only patch when the computed
    // value actually changes. This is what makes it safe to include
    // calories_estimated in the dep list (the effect re-runs after our
    // own patch but immediately returns).
    if (next === cardio.calories_estimated) return
    onChange({ calories_estimated: next })
  }, [
    cardio.cardio_type,
    cardio.duration_min,
    cardio.speed,
    cardio.level,
    cardio.calories_estimated,
    bodyWeightKg,
    onChange,
  ])

  return (
    <Card className="border-blue-500/30">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground">{t('workout.cardio.title')}</p>
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-red-500 p-1"
            aria-label={t('workout.session.removeExercise')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          {TYPES.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onChange({ cardio_type: id })}
              className={cn(
                'h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
                cardio.cardio_type === id
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {t(`workout.cardioType.${id}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <NumField
            label={t('workout.cardio.duration')}
            value={cardio.duration_min}
            onChange={(v) => onChange({ duration_min: v })}
            step="1"
          />
          {fields.level && (
            <NumField
              label={t('workout.cardio.level')}
              value={cardio.level}
              onChange={(v) => onChange({ level: v })}
              step="1"
            />
          )}
          {fields.incline && (
            <NumField
              label={t('workout.cardio.incline')}
              value={cardio.incline}
              onChange={(v) => onChange({ incline: v })}
              step="0.5"
            />
          )}
          {fields.speed && (
            <NumField
              label={t('workout.cardio.speed')}
              value={cardio.speed}
              onChange={(v) => onChange({ speed: v })}
              step="0.1"
            />
          )}
          {fields.distance && (
            <NumField
              label={t('workout.cardio.distance')}
              value={cardio.distance_km}
              onChange={(v) => onChange({ distance_km: v })}
              step="0.1"
            />
          )}
          {fields.calories && (
            <NumField
              label={t('workout.cardio.calories')}
              value={cardio.calories_estimated}
              onChange={handleCaloriesChange}
              step="1"
            />
          )}
        </div>

        <textarea
          value={cardio.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder={t('workout.cardio.notesPlaceholder')}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
          rows={2}
          aria-label={t('workout.cardio.notes')}
        />
      </CardContent>
    </Card>
  )
}
